import {
  CreateFolderResponse,
  type GetFolderDetailsResponse,
} from "@mg/schemas/src/christo/catalyst";
import { useMutation } from "@tanstack/react-query";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type FC,
  type PropsWithChildren,
} from "react";

import { ChatMessageProps } from "./ChatMessage";
import { dpaDemoComments } from "./demo/dpa";
import DocumentStatusUpdater from "./DocumentStatusUpdater";

import { indexRoute } from "../../routes/index/route";
import { ticketRoute } from "../../routes/tickets/routes/ticket/route";
import { getProject } from "../../services/projects";
import { uploadFile } from "../../services/upload";
import {
  createReviewStream,
  getReviewTopics,
  summarizeFolder,
} from "../../services/workflows";
import { isNil } from "../../utils/fp";
import { createCombinatorString } from "../../utils/helpers/arrays";
import {
  useCreateFolderMutation,
  useProjectMutation,
  useRevisionMutation,
} from "../../utils/queries/projects";
import { queryClient } from "../../utils/queryClient";
import { generateUUID } from "../../utils/uuid";

export type ReviewComment = {
  _id: string;
  comment: string;
  persona: string;
  required: boolean;
  x: number;
  y: number;
  page_num: number;
  timestamp: number; // offset in seconds
};

type ReviewDocument = {
  /**
   * A reference to the original file; only applies to newly uploaded assets.
   */
  file: File | undefined;
  /**
   * A reference to a message with the given ID. Useful for when we're
   * streaming responses from Rembrandt and need to update the currently
   * displayed message of a comment.
   *
   * Note: messages created from uploading a review document cannot be created
   * with the `addMessage` function as that doesn't give us access to the
   * generated messageId. Messages are inserted manually as part of the document
   * review process.
   */
  messageId: string | undefined;
  /**
   * Since tickets are created for every file that's uploaded, this is a
   * reference to the ticketId that is created for the file.
   */
  ticketId: string;
  /**
   * All tickets must also have a created revision (or version) board so we
   * know which thumbnails to reference later, and to tell the AI which board to
   * actually run a review on. If this value came as a result of getting Folder
   * details, it will always be the boardId of the most recent revision (or
   * version)
   */
  boardId: string;
  /**
   * An array of image thumbs where the review file images were uploaded in S3.
   * This will always be the full image path and will always start with
   * "https://"
   */
  thumbnails: string[];
  /**
   * An AI summary of the specific document's review.
   */
  status: string;
  /**
   * The processing status of a document and whether or not we're able to
   * display the thumbnail images or run an AI review on the document.
   */
  processStatus: {
    isPending: boolean; // true - document analysis in progress; false - thumbnails generated
    isAIReady: boolean; // false - layout analysis in progrgess; true - AI review ready
  };
  /**
   * An array of potential comments to show on the preview pane. The comments
   * here are not final and will be replaced with the final comments in the
   * preview pane when they become available.
   */
  pendingComments: ReviewComment[] | null;
  /**
   * The final comments used to replace the pending comments in the preview
   * pane.
   */
  finalComments: ReviewComment[];
  /**
   * Whether or not the AI review has run successfully AND completed.
   */
  isDone: boolean;
};

type WorkflowContextValues = {
  /**
   * A handler to add a message. The message should not include an `id` as one
   * is assigned upon entry. Returns the ID of the message.
   */
  addMessage(message: Omit<ChatMessageProps, "id">): string;
  /**
   * The number of current tickets in the folder, or the number of files the
   * user just selected for upload.
   */
  assetsCount: number;
  /**
   * The index of the currently-previewing asset on the right pane.
   */
  assetPreviewIndex: number;
  /**
   * Handler to decrement the preview document index on the right pane.
   */
  decrementPreviewIndex(): void;
  /**
   * A handler to edit a message with a given message ID. The second argument
   * only needs to partially represent the original object as the original and
   * new messages will be combined.
   */
  editMessage(
    messageId: string,
    message: Partial<Omit<ChatMessageProps, "id">>,
  ): void;
  /**
   * Handler to increment the preview document index on the right pane.
   */
  incrementPreviewIndex(): void;
  /**
   * A reference to the current folder ID if exists, otherwise one will be
   * created when the user uploads the first batch of files.
   */
  folderId: string | undefined;
  /**
   * An optional folder detail, otherwise will eventually be set when the user
   * uploads the first batch of files.
   */
  folder: GetFolderDetailsResponse | null;
  /**
   * The current list of chat messages
   */
  messages: ChatMessageProps[];
  /**
   * An optional array of personas. This is optional because when running a
   * fresh workflow, we're guaranteed to have them. But if we're running a
   * subsequent workflow on an existing folder, then we can do without this.
   */
  personas: ("brand" | "copy" | "legal")[] | undefined;
  /**
   * An array of both previously reviewed ticket assets and newly added assets.
   * You can expect the shapes to be identical regardless.
   */
  reviewDocuments: ReviewDocument[];
  /**
   * A handler to process file uploads. If no `folderId` was provided in the
   * initialization of this component, a new folder will be created. Each file
   * will then create a new ticket within that folder in addition to the ticket's
   * first revision. Each file from here will be processed in parallel. When a
   * file's status is no longer pending and the AI cache has been populated, the
   * AI review will run automatically. This is a fairly loaded handler,
   * but it handles all the complexity of file uploads in one place.
   *
   * Note: zip files will create a single ticket. This hasn't been fully tested
   * with folder uploads themselves, but that would disqualify this folder being
   * a leaf.
   */
  uploadFiles(files: FileList | undefined | null): Promise<void>;
  /**
   * The index of the currently-previewing comment in the pending comments array.
   */
  pendingCommentIndex: number;
  /**
   * A handler to increment the pending comment index.
   */
  incrementActiveCommentIndex(nmb: number): void;
  /**
   * An array of review topics.
   */
  reviewTopics: string[];
};

const WorkflowContext = createContext({
  addMessage: () => {
    console.warn("Unimplemented");
    return "Unimplemented";
  },
  assetsCount: 0,
  assetPreviewIndex: 0,
  decrementPreviewIndex: () => {
    console.warn("Unimplemented");
  },
  editMessage: () => {
    console.warn("Unimplemented");
  },
  folder: null,
  folderId: undefined,
  incrementPreviewIndex: () => {
    console.warn("Unimplemented");
  },
  messages: [],
  personas: [],
  reviewDocuments: [],
  uploadFiles: async () => {
    console.warn("Unimplemented");
  },
  pendingCommentIndex: 0,
  incrementActiveCommentIndex: (nmb: number) => {
    console.warn("Unimplemented", nmb);
  },
  reviewTopics: [],
} as WorkflowContextValues);

export const WorkflowProvider: FC<
  PropsWithChildren<
    Pick<WorkflowContextValues, "folderId" | "folder" | "personas">
  >
> = ({ children, folder: initialFolder, folderId, personas }) => {
  // Router methods
  const navigate = useNavigate();
  const { matches } = useRouterState();

  // Queries and mutations
  const createFolderMutation = useCreateFolderMutation();
  const ticketMutation = useProjectMutation();
  const revisionMutation = useRevisionMutation();
  const summarizeFolderMutation = useMutation({
    mutationKey: ["folders", "summarize", folderId],
    mutationFn: summarizeFolder,
  });

  // Local state
  const [assetsCount, setAssetsCount] = useState(
    initialFolder?.tickets?.length ?? 0,
  );
  const [assetPreviewIndex, setAssetPreviewIndex] = useState(0);
  const [didUpload, setDidUpload] = useState(false);
  const [folder, setFolder] = useState(initialFolder); // sacred; do not pass setter into context
  // @ts-expect-error TS doesn't like the extra stuff
  const [messages, setMessages] = useState<ChatMessageProps[]>(() => {
    if (isNil(initialFolder)) return [];

    if (isNil(folderId) && (isNil(personas) || personas.length === 0)) {
      return [
        {
          id: generateUUID(),
          message:
            "In order to review your assets, I need to better understand how to review your assets.",
          type: "message-simple",
          author: "theirs",
          actions: [
            {
              label: "Pick a persona",
              type: "back",
              onClick: () =>
                // TODO: Probably not the exact behavior we want, but it works.
                navigate({
                  to: indexRoute.to,
                }),
            },
          ],
        },
      ];
    }

    if (
      "workflowResults" in initialFolder &&
      initialFolder.workflowResults.length > 0
    ) {
      return initialFolder.workflowResults.flatMap((result) => {
        const tickets = result.reviewDocuments;
        const { summary } = result;

        return [
          ...tickets.map((ticket, i) => ({
            author: "theirs" as const,
            id: generateUUID(),
            type: "message-simple",
            actions: [
              {
                label: "View ticket",
                onClick() {
                  navigate({
                    to: ticketRoute.to,
                    params: { ticketId: ticket.ticketId },
                    search: (prev) => ({ ...prev, view: "workflow" }),
                  });
                },
              },
            ],
            message: `Asset ${i + 1}: ${ticket.finalComments.length} comments`,
          })),
          {
            author: "theirs",
            id: generateUUID(),
            message: summary,
            type: "message-simple",
          },
        ] as ChatMessageProps[];
      });
    }

    if (
      "workflowResults" in initialFolder &&
      initialFolder.workflowResults.length === 0 &&
      initialFolder.tickets?.length
    ) {
      let localMessageId = generateUUID();
      return [
        {
          id: localMessageId,
          author: "theirs",
          message: `Looks like this still needs some AI magic!`,
          type: "message-simple",
          actions: [
            {
              label: "Start an AI review",
              onClick: () => {
                initialFolder.tickets?.forEach((t, i) => {
                  const ticketId = t._id;

                  if (i === 0) {
                    editMessage(localMessageId, {
                      author: "theirs",
                      message: "Generating comments...",
                      type: "status",
                    });
                  } else {
                    localMessageId = addMessage({
                      author: "theirs",
                      message: "Generating comments...",
                      type: "status",
                    });
                  }
                  setDidUpload(true);
                  createReviewStream(
                    {
                      ticketId,
                      boardId: reviewDocuments.find(
                        (d) => d.ticketId === ticketId,
                      )!.boardId,
                      personas: personas as ("copy" | "brand" | "legal")[],
                    },
                    (chunk) => {
                      if (chunk.data.isDone) {
                        setReviewDocuments((prev) => {
                          const foundIndex = prev.findIndex(
                            (p) => p.ticketId === ticketId,
                          );

                          if (foundIndex === -1) {
                            throw new Error(
                              "Unable to update the status of a ticket that does not exist.",
                            );
                          }

                          return prev.toSpliced(foundIndex, 1, {
                            ...prev[foundIndex],
                            isDone: true,
                            status: chunk.data.status,
                            pendingComments: [],
                            finalComments: chunk.data.finalComments,
                          });
                        });

                        return editMessage(localMessageId!, {
                          message: chunk.data.status,
                          type: "status-done",
                        });
                      }

                      setReviewDocuments((prev) => {
                        const foundIndex = prev.findIndex(
                          (p) => p.ticketId === ticketId,
                        );

                        if (foundIndex === -1) {
                          throw new Error(
                            "Unable to update the status of a ticket that does not exist.",
                          );
                        }

                        return prev.toSpliced(foundIndex, 1, {
                          ...prev[foundIndex],
                          status: chunk.data.status,
                          pendingComments: chunk.data.pendingComments,
                          messageId: localMessageId,
                        });
                      });

                      const lastComment =
                        chunk.data.pendingComments.at(-1)?.comment;

                      // TODO: Update to match the behavior described at
                      // https://mg-internal.slack.com/archives/C05DWHZLH24/p1744311072528099?thread_ts=1744303279.854639&cid=C05DWHZLH24
                      editMessage(localMessageId!, {
                        message: `${chunk.data.status}\n${isNil(lastComment) ? "No issues found yet." : lastComment}`,
                      });
                    },
                  );
                });
              },
            },
          ],
        },
        ...(initialFolder.tickets ?? []).map((t) => ({
          id: generateUUID(),
          author: "theirs",
          message: `You have uploaded ${t.title}`,
          type: "message-simple",
          actions: [
            {
              label: "View ticket",
              onClick: () => {
                navigate({ to: ticketRoute.to, params: { ticketId: t._id } });
              },
            },
          ],
        })),
      ];
    }

    if (
      window.location.pathname.includes("tickets") &&
      (personas ?? []).length > 0
    ) {
      return [
        {
          id: generateUUID(),
          author: "mine",
          message: "Please review the following assets from Figma.",
          type: "message-simple",
        },
        {
          id: generateUUID(),
          author: "theirs",
          message:
            "Okay. I'll analyze your Figma import and review it automatically for you. One moment...",
          type: "message-simple",
        },
      ] as ChatMessageProps[];
    }

    return [];
  });
  const [reviewDocuments, setReviewDocuments] = useState<ReviewDocument[]>(
    () => {
      if (isNil(initialFolder)) {
        return [];
      }

      if (
        !isNil(initialFolder.workflowResults) &&
        !isNil(initialFolder.tickets)
      ) {
        // We want to generate the map based on tickets array over
        // `workflowResults` since it's entirely possible to have tickets but have
        // not yet run a workflow on the folder.
        return initialFolder.tickets.map((ticket) => {
          const allResults = initialFolder.workflowResults.flatMap(
            (r) => r.reviewDocuments,
          );
          const lastTicketReview = allResults.findLast(
            (l) => l.ticketId === ticket._id,
          );

          const {
            _id: boardId,
            isPending,
            isAIReady,
          } = ticket.lastRevisionBoard;

          return {
            ticketId: ticket._id,
            boardId: boardId,
            thumbnails: ticket.thumbnails,
            processStatus: {
              isPending,
              isAIReady,
            },
            pendingComments: [],
            file: undefined,
            messageId: undefined,
            isDone: lastTicketReview?.isDone ?? false,
            status: lastTicketReview?.status ?? "",
            finalComments: lastTicketReview?.finalComments ?? [],
          } as ReviewDocument;
        });
      }

      return [];
    },
  );
  const [pendingCommentIndex, setPendingCommentIndex] = useState(0);
  const [reviewTopics, setReviewTopics] = useState<string[]>([]);
  // Effect chain
  useEffect(() => {
    if (
      reviewDocuments.length > 0 &&
      reviewDocuments.every((d) => d.isDone === true) &&
      didUpload
    ) {
      if (isNil(folderId)) {
        throw new Error("Attempting to summarize a folder that doesn't exist.");
      }

      summarizeFolderMutation.mutate(folderId, {
        onSuccess(data) {
          if (isNil(data)) return;

          // All documents have been processed, we can now add a completion message
          addMessage({
            author: "theirs",
            type: "message-confetti",
            message: data.summary,
            actions: [
              {
                label: "View the comments",
                onClick() {
                  navigate({
                    to: ticketRoute.to,
                    params: { ticketId: reviewDocuments[0].ticketId },
                    search: (prev) => ({ ...prev, view: "workflow" }),
                  });
                },
              },
            ],
          });
          setDidUpload(false);
        },
      });
    }
  }, [didUpload, folderId, navigate, reviewDocuments]);

  useEffect(() => {
    getReviewTopics(personas ?? []).then((topics) => {
      setReviewTopics(topics as string[]);
    });
  }, [personas]);
  // Handlers
  const incrementPreviewIndex = useCallback(() => {
    setAssetPreviewIndex((prev) => Math.min(prev + 1, assetsCount - 1));
  }, [assetsCount]);

  const decrementPreviewIndex = useCallback(() => {
    setAssetPreviewIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  const addMessage = useCallback((message: Omit<ChatMessageProps, "id">) => {
    const newMessageId = generateUUID();
    setMessages((prev) => [...prev, { id: newMessageId, ...message }]);

    return newMessageId;
  }, []);

  const editMessage = useCallback(
    (messageId: string, message: Partial<Omit<ChatMessageProps, "id">>) => {
      setMessages((prev) => {
        const foundIndex = prev.findIndex((m) => m.id === messageId);

        if (foundIndex === -1) {
          console.warn(
            `Attempt to edit chat message "${messageId}", but no chat message exists with that ID.`,
          );
          return prev;
        }

        return prev.toSpliced(foundIndex, 1, {
          ...prev[foundIndex],
          ...message,
        });
      });
    },
    [],
  );

  const uploadFiles = async (files: FileList | undefined | null) => {
    if (isNil(files) || files.length === 0) {
      addMessage({
        author: "theirs",
        type: "message-simple",
        message:
          "I didn't quite catch that. Can you try uploading your assets again?",
      });
      return;
    }

    // Demo doc stuff
    const initialFile = files[0];
    if (initialFile.name.toLocaleLowerCase().includes("dpa template")) {
      const dpaMessageId = generateUUID();

      setAssetsCount(1);
      setDidUpload(true);

      setMessages((prev) => [
        ...prev,
        {
          id: generateUUID(),
          author: "mine",
          message: "Please upload and review the following assets.",
          files: Array.from(files).map((f) => f.name),
          type: "message-simple",
        },
        {
          id: dpaMessageId,
          message: undefined,
          author: "theirs",
          type: "upload",
          files: [initialFile.name],
        },
      ]);
      setReviewDocuments([
        {
          ticketId: "editabledoc",
          boardId: "editabledoc",
          file: initialFile,
          finalComments: [],
          isDone: false,
          messageId: dpaMessageId,
          pendingComments: dpaDemoComments,
          processStatus: {
            isPending: false,
            isAIReady: true,
          },
          status: "",
          thumbnails: [
            "puntt/pdfs/dev/67d85d4544e24aaf1eb1fbd1/67d85d4544e24aaf1eb1fbd1_page_1_1224x1584.png",
            "puntt/pdfs/dev/67d85d4544e24aaf1eb1fbd1/67d85d4544e24aaf1eb1fbd1_page_2_1224x1584.png",
            "puntt/pdfs/dev/67d85d4544e24aaf1eb1fbd1/67d85d4544e24aaf1eb1fbd1_page_3_1224x1584.png",
            "puntt/pdfs/dev/67d85d4544e24aaf1eb1fbd1/67d85d4544e24aaf1eb1fbd1_page_4_1224x1584.png",
          ],
        },
      ]);

      setTimeout(() => {
        setReviewDocuments(() => [
          {
            ticketId: "editabledoc",
            boardId: "editabledoc",
            file: initialFile,
            finalComments: dpaDemoComments,
            isDone: false,
            messageId: dpaMessageId,
            pendingComments: [],
            processStatus: {
              isPending: false,
              isAIReady: true,
            },
            status: "",
            thumbnails: [
              "puntt/pdfs/dev/67d85d4544e24aaf1eb1fbd1/67d85d4544e24aaf1eb1fbd1_page_1_1224x1584.png",
              "puntt/pdfs/dev/67d85d4544e24aaf1eb1fbd1/67d85d4544e24aaf1eb1fbd1_page_2_1224x1584.png",
              "puntt/pdfs/dev/67d85d4544e24aaf1eb1fbd1/67d85d4544e24aaf1eb1fbd1_page_3_1224x1584.png",
              "puntt/pdfs/dev/67d85d4544e24aaf1eb1fbd1/67d85d4544e24aaf1eb1fbd1_page_4_1224x1584.png",
            ],
          },
        ]);
      }, 15_000); // There are 7 comments, each comment shows for 2 seconds. Last comment will have a 1sec delay after it.

      return; // Do not proceed with the rest of the process. This file was a demo.
    }
    if (initialFile.name.toLocaleLowerCase().includes("joke list")) {
      const dpaMessageId = generateUUID();

      setAssetsCount(1);
      setDidUpload(true);

      setMessages((prev) => [
        ...prev,
        {
          id: generateUUID(),
          author: "mine",
          message: "Please upload and review the following assets.",
          files: Array.from(files).map((f) => f.name),
          type: "message-simple",
        },
        {
          id: dpaMessageId,
          message: undefined,
          author: "theirs",
          type: "upload",
          files: [initialFile.name],
        },
      ]);
      setReviewDocuments([
        {
          ticketId: "editabledoc2",
          boardId: "editabledoc2",
          file: initialFile,
          finalComments: [],
          isDone: false,
          messageId: dpaMessageId,
          pendingComments: dpaDemoComments,
          processStatus: {
            isPending: false,
            isAIReady: true,
          },
          status: "",
          thumbnails: [
            "puntt/pdfs/dev/67d9a7ded56573a2579aba9e/67d9a7ded56573a2579aba9e_page_1_1224x1584.png",
            "puntt/pdfs/dev/67d9a7ded56573a2579aba9e/67d9a7ded56573a2579aba9e_page_2_1224x1584.png",
            "puntt/pdfs/dev/67d9a7ded56573a2579aba9e/67d9a7ded56573a2579aba9e_page_3_1224x1584.png",
            "puntt/pdfs/dev/67d9a7ded56573a2579aba9e/67d9a7ded56573a2579aba9e_page_4_1224x1584.png",
            "puntt/pdfs/dev/67d9a7ded56573a2579aba9e/67d9a7ded56573a2579aba9e_page_5_1224x1584.png",
          ],
        },
      ]);

      setTimeout(() => {
        setReviewDocuments(() => [
          {
            ticketId: "editabledoc2",
            boardId: "editabledoc2",
            file: initialFile,
            finalComments: dpaDemoComments,
            isDone: false,
            messageId: dpaMessageId,
            pendingComments: [],
            processStatus: {
              isPending: false,
              isAIReady: true,
            },
            status: "",
            thumbnails: [
              "puntt/pdfs/dev/67d9a7ded56573a2579aba9e/67d9a7ded56573a2579aba9e_page_1_1224x1584.png",
              "puntt/pdfs/dev/67d9a7ded56573a2579aba9e/67d9a7ded56573a2579aba9e_page_2_1224x1584.png",
              "puntt/pdfs/dev/67d9a7ded56573a2579aba9e/67d9a7ded56573a2579aba9e_page_3_1224x1584.png",
              "puntt/pdfs/dev/67d9a7ded56573a2579aba9e/67d9a7ded56573a2579aba9e_page_4_1224x1584.png",
              "puntt/pdfs/dev/67d9a7ded56573a2579aba9e/67d9a7ded56573a2579aba9e_page_5_1224x1584.png",
            ],
          },
        ]);
      }, 15_000); // There are 7 comments, each comment shows for 2 seconds. Last comment will have a 1sec delay after it.

      return; // Do not proceed with the rest of the process. This file was a demo.
    }

    setAssetsCount((prev) => prev + files.length);
    setDidUpload(true);

    let currentFolder:
      | GetFolderDetailsResponse
      | CreateFolderResponse
      | null
      | undefined = folder;

    // This will only be the case outside of `/projects`.
    if (isNil(folderId)) {
      if (isNil(personas) || personas.length === 0) {
        throw new Error(
          "In order to create a folder and review the selected files, at least one persona must have been chosen.",
        );
      }

      currentFolder = await createFolderMutation.mutateAsync({
        folder: {
          name: `${createCombinatorString(personas)} Review ${new Date().toISOString().slice(0, 10)}`,
          personas,
        },
      });

      setFolder({
        ...(currentFolder as CreateFolderResponse).folder,
        // TODO: we can just add this to the CreateFolderResponse parser
        workflowResults: [],
        tickets: [],
      });

      navigate({
        // @ts-expect-error TS2322: we can probably just put `folderId` on the
        // main route.
        search(prev) {
          return {
            ...prev,
            folderId: (currentFolder as CreateFolderResponse)!.folder._id,
          };
        },
        replace: true,
      });
    }

    // if after all that, the folderId is still nullish, we need to exit.
    if (
      isNil(folderId) &&
      isNil((currentFolder as GetFolderDetailsResponse)?._id) &&
      isNil((currentFolder as CreateFolderResponse)?.folder?._id)
    ) {
      throw new Error("Unable to add tickets to a project that doesn't exist.");
    }

    const fileList = Array.from(files);
    const messageIds = fileList.map(() => generateUUID());

    // Next, we create as many messages as we have files. We won't use the
    // `addMessage` function since we need a reference to the messageIds we just
    // created later.
    setMessages((prev) => [
      ...prev,
      {
        id: generateUUID(),
        author: "mine",
        message: "Please upload and review the following assets.",
        files: fileList.map((f) => f.name),
        type: "message-simple",
      },
      ...messageIds.map(
        (id, i) =>
          ({
            id,
            message: undefined,
            author: "theirs",
            type: "upload",
            files: [files[i].name],
          }) satisfies ChatMessageProps,
      ),
    ]);

    Promise.all(
      fileList.map((file, i) =>
        handleFile(
          file,
          ((currentFolder as unknown as CreateFolderResponse)?.folder?._id ??
            folderId) as string,
          messageIds[i],
        ),
      ),
    );
  };

  async function handleFile(file: File, folderId: string, messageId: string) {
    // 1: actually upload the file
    const payload = await uploadFile(file, {}, true);

    const source = payload.location;
    // 1.a: Update the associated message's status
    editMessage(messageId, {
      message: "Creating ticket",
      type: "status",
    });

    // 2: create a ticket for the file.
    const ticket = await ticketMutation.mutateAsync({
      title: file.name,
      folder: folderId,
    });

    // 3: create a revision
    const revision = await revisionMutation.mutateAsync({
      ticketId: ticket._id,
      payload: {
        name: "Version 1",
        documentImportType: file.name
          .slice(file.name.lastIndexOf(".") + 1)
          .toUpperCase(),
        reviewFiles: [
          {
            source,
            type: file.type.includes("image")
              ? "image"
              : file.type.includes("video")
                ? "video"
                : "file",
          },
        ],
      },
    });

    const folderTicket: NonNullable<
      GetFolderDetailsResponse["tickets"]
    >[number] = {
      title: ticket.title,
      thumbnails: ticket.thumbnails,
      _id: ticket._id,
      lastRevisionBoard: revision.boards[0],
    };

    setFolder((prev) => ({
      ...prev!,
      tickets: [...(prev?.tickets ?? []), folderTicket],
    }));

    // 3.a: Update the associated message's status
    editMessage(messageId, {
      message: "Generating thumbnails",
      type: "status",
    });

    setReviewDocuments((prev) => [
      ...prev,
      {
        file,
        messageId,
        status: "",
        ticketId: ticket._id,
        boardId: revision.boards[0]._id, // only one will be created at this time.
        thumbnails: [],
        processStatus: {
          isPending: true,
          isAIReady: false,
        },
        pendingComments: null,
        finalComments: [],
        isDone: false,
      },
    ]);
  }

  const values = useMemo(
    () => ({
      addMessage,
      assetPreviewIndex,
      assetsCount,
      decrementPreviewIndex,
      editMessage,
      folder,
      folderId,
      incrementPreviewIndex,
      messages,
      personas,
      reviewDocuments,
      uploadFiles,
      pendingCommentIndex,
      incrementActiveCommentIndex: setPendingCommentIndex,
      reviewTopics,
    }),
    [
      addMessage,
      assetPreviewIndex,
      assetsCount,
      decrementPreviewIndex,
      editMessage,
      folder,
      folderId,
      incrementPreviewIndex,
      messages,
      personas,
      reviewDocuments,
      uploadFiles,
      pendingCommentIndex,
      setPendingCommentIndex,
      reviewTopics,
    ],
  );

  return (
    <WorkflowContext.Provider value={values}>
      {reviewDocuments.map(
        ({ messageId, boardId, ticketId, processStatus, isDone }) => (
          <DocumentStatusUpdater
            key={ticketId}
            isDone={isDone}
            needsRun={
              matches.some((m) => m.pathname.includes("tickets")) &&
              (personas ?? []).length > 0
            }
            ticketId={ticketId}
            statuses={processStatus}
            revisionId={boardId}
            onFail={() => console.error("Max checks exceeded")}
            onReady={() => {
              editMessage(messageId!, {
                message: "Analyzing document.",
              });

              queryClient
                .fetchQuery({
                  queryKey: ["tickets", ticketId],
                  queryFn: () => getProject(ticketId),
                })
                .then((ticket) => {
                  setReviewDocuments((prev) => {
                    const updatedDocuments = [...prev];
                    const documentIndex = updatedDocuments.findIndex(
                      (doc) => doc.ticketId === ticketId,
                    );

                    if (documentIndex !== -1) {
                      updatedDocuments[documentIndex].processStatus.isPending =
                        false;
                      updatedDocuments[documentIndex].thumbnails =
                        ticket.revisionBoards[0].thumbnails;
                    }

                    return updatedDocuments;
                  });
                });
            }}
            onAIReady={() => {
              let localMessageId = messageId;
              if (
                matches.some((m) => m.pathname.includes("tickets")) &&
                (personas ?? []).length > 0
              ) {
                localMessageId = addMessage({
                  author: "theirs",
                  message: "Generating comments...",
                  type: "status",
                });
                setDidUpload(true);
                setReviewDocuments((prev) => {
                  const updatedDocuments = [...prev];
                  const documentIndex = updatedDocuments.findIndex(
                    (doc) => doc.ticketId === ticketId,
                  );
                  if (documentIndex !== -1) {
                    updatedDocuments[documentIndex].messageId = localMessageId;
                  }
                  return updatedDocuments;
                });
              } else {
                editMessage(localMessageId!, {
                  message: "Generating comments...",
                });
              }

              setReviewDocuments((prev) => {
                const updatedDocuments = [...prev];
                const documentIndex = updatedDocuments.findIndex(
                  (doc) => doc.ticketId === ticketId,
                );

                if (documentIndex !== -1) {
                  updatedDocuments[documentIndex].processStatus.isAIReady =
                    true;
                }

                return updatedDocuments;
              });

              createReviewStream(
                {
                  ticketId,
                  boardId: reviewDocuments.find((d) => d.ticketId === ticketId)!
                    .boardId,
                  personas: personas as ("copy" | "brand" | "legal")[],
                },
                (chunk) => {
                  if (chunk.data.isDone) {
                    setReviewDocuments((prev) => {
                      const foundIndex = prev.findIndex(
                        (p) => p.ticketId === ticketId,
                      );

                      if (foundIndex === -1) {
                        throw new Error(
                          "Unable to update the status of a ticket that does not exist.",
                        );
                      }

                      return prev.toSpliced(foundIndex, 1, {
                        ...prev[foundIndex],
                        isDone: true,
                        status: chunk.data.status,
                        pendingComments: [],
                        finalComments: chunk.data.finalComments,
                      });
                    });

                    return editMessage(localMessageId!, {
                      message: chunk.data.status,
                      type: "status-done",
                    });
                  }

                  setReviewDocuments((prev) => {
                    const foundIndex = prev.findIndex(
                      (p) => p.ticketId === ticketId,
                    );

                    if (foundIndex === -1) {
                      throw new Error(
                        "Unable to update the status of a ticket that does not exist.",
                      );
                    }

                    return prev.toSpliced(foundIndex, 1, {
                      ...prev[foundIndex],
                      status: chunk.data.status,
                      pendingComments: chunk.data.pendingComments,
                    });
                  });

                  const lastComment =
                    chunk.data.pendingComments.at(-1)?.comment;

                  // TODO: Update to match the behavior described at
                  // https://mg-internal.slack.com/archives/C05DWHZLH24/p1744311072528099?thread_ts=1744303279.854639&cid=C05DWHZLH24
                  editMessage(localMessageId!, {
                    message: `${chunk.data.status}\n${isNil(lastComment) ? "No issues found yet." : lastComment}`,
                  });
                },
              );
            }}
          />
        ),
      )}
      {children}
    </WorkflowContext.Provider>
  );
};

export const useWorkflow = () => useContext(WorkflowContext);
